import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

import { TAG_COLORS, type BnTagColor } from "./bn-tag.js";

let uid = 0;

/**
 * bn-color-picker is the swatch grid for the eight tag colors. It mirrors the
 * choice on `value` and emits a composed `change` CustomEvent, so pages bind it
 * like `.value=${c} @change=${(e) => (c = e.detail.value)}`.
 *
 * It is built on eight native radios sharing a name — the same visually-hidden-
 * input technique bn-checkbox uses — rather than on buttons and a keydown
 * handler: that way arrow-key navigation, roving focus and radiogroup semantics
 * all come from the platform. Radio grouping by `name` is scoped per shadow
 * tree, so several pickers on one page do not interfere with each other.
 *
 * Color is never the only carrier of meaning here: every swatch has the color's
 * name as its accessible name and its tooltip, and the chosen one shows a check.
 */
@customElement("bn-color-picker")
export class BnColorPicker extends LitElement {
  @property() value: BnTagColor = "gray";
  /**
   * Accessible name for the group. bn-field's <label> is not for-associated with
   * its slotted control anywhere in this app, so controls carry their own.
   */
  @property() label = "Color";
  @property({ type: Boolean }) disabled = false;

  private readonly groupName = `bn-color-${++uid}`;

  static override styles = css`
    :host {
      display: block;
    }
    .grid {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .sw {
      display: inline-flex;
      cursor: pointer;
    }
    .sw.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    /* Visually hidden but focusable: the label forwards clicks, the dot below
       renders the state, and :focus-visible reaches it via the sibling. */
    input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      margin: 0;
      pointer-events: none;
    }
    .dot {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      box-shadow: inset 0 0 0 1px rgba(10, 13, 15, 0.08);
      transition: box-shadow var(--bino-transition-fast);
    }
    input:focus-visible + .dot {
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .dot svg {
      display: none;
      width: 0.875rem;
      height: 0.875rem;
      stroke-width: 2.5;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    input:checked + .dot svg {
      display: block;
    }
    .gray {
      background: var(--bino-tag-gray-bg);
    }
    .gray svg,
    input:checked + .dot.gray {
      stroke: var(--bino-tag-gray-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-gray-fg);
    }
    .celeste {
      background: var(--bino-tag-celeste-bg);
    }
    .celeste svg,
    input:checked + .dot.celeste {
      stroke: var(--bino-tag-celeste-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-celeste-fg);
    }
    .yellow {
      background: var(--bino-tag-yellow-bg);
    }
    .yellow svg,
    input:checked + .dot.yellow {
      stroke: var(--bino-tag-yellow-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-yellow-fg);
    }
    .magenta {
      background: var(--bino-tag-magenta-bg);
    }
    .magenta svg,
    input:checked + .dot.magenta {
      stroke: var(--bino-tag-magenta-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-magenta-fg);
    }
    .green {
      background: var(--bino-tag-green-bg);
    }
    .green svg,
    input:checked + .dot.green {
      stroke: var(--bino-tag-green-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-green-fg);
    }
    .red {
      background: var(--bino-tag-red-bg);
    }
    .red svg,
    input:checked + .dot.red {
      stroke: var(--bino-tag-red-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-red-fg);
    }
    .blue {
      background: var(--bino-tag-blue-bg);
    }
    .blue svg,
    input:checked + .dot.blue {
      stroke: var(--bino-tag-blue-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-blue-fg);
    }
    .purple {
      background: var(--bino-tag-purple-bg);
    }
    .purple svg,
    input:checked + .dot.purple {
      stroke: var(--bino-tag-purple-fg);
      box-shadow: 0 0 0 2px var(--bino-tag-purple-fg);
    }
    /* Focus must win over the selected ring, or a keyboard user tabbing across
       the group cannot see where they are. */
    input:focus-visible + .dot {
      box-shadow:
        0 0 0 2px currentColor,
        0 0 0 5px var(--bino-focus-ring);
    }
  `;

  private onChange(e: Event) {
    // The native `change` is not composed so it never leaves the shadow root —
    // the stop is belt-and-braces, matching bn-checkbox.
    e.stopPropagation();
    this.value = (e.target as HTMLInputElement).value as BnTagColor;
    this.dispatchEvent(
      new CustomEvent("change", { detail: { value: this.value }, bubbles: true, composed: true }),
    );
  }

  override render() {
    return html`<div class="grid" role="radiogroup" aria-label=${this.label}>
      ${TAG_COLORS.map(
        (c) => html`<label class="sw ${this.disabled ? "disabled" : ""}" title=${c}>
          <input
            type="radio"
            name=${this.groupName}
            value=${c}
            aria-label=${c}
            .checked=${this.value === c}
            ?disabled=${this.disabled}
            @change=${this.onChange}
          />
          <span class="dot ${c}">
            <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.5 5 9l4.5-6"></path></svg>
          </span>
        </label>`,
      )}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-color-picker": BnColorPicker;
  }
}
